import { getCelebrationHoldMs } from "./celebrationTiming";
import { memo, useEffect, useRef, useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import type { ReactNode } from "react";
import type { IntakeData } from "@/lib/types";
import { cn } from "@/lib/utils";
import Lottie from "lottie-react";

interface SubmitCelebrationCardProps {
  data: Pick<IntakeData, "firstName">;
  onComplete?: () => void;
  autoCompleteMs?: number;
  showVerifyHint?: boolean;
  footerActions?: ReactNode;
  showInboxButton?: boolean;
  descriptionContent?: ReactNode;
  /** Merged onto the root panel (e.g. tighter padding on mobile). */
  className?: string;
}

export const SubmitCelebrationCard = memo(function SubmitCelebrationCard({
  data,
  onComplete,
  autoCompleteMs,
  showVerifyHint = true,
  footerActions,
  showInboxButton = true,
  descriptionContent,
  className,
}: SubmitCelebrationCardProps) {
  const { i18n } = useLingui();
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [successAnimation, setSuccessAnimation] = useState<object | null>(null);

  useEffect(() => {
    let isMounted = true;
    const loadAnimation = async () => {
      try {
        const response = await fetch("/Success.json");
        if (!response.ok) return;
        const animationJson = (await response.json()) as object;
        if (isMounted) {
          setSuccessAnimation(animationJson);
        }
      } catch {
        // Keep fallback check icon when animation cannot be loaded.
      }
    };
    void loadAnimation();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!onCompleteRef.current) return;
    const ms = getCelebrationHoldMs(autoCompleteMs);
    const id = window.setTimeout(() => {
      onCompleteRef.current?.();
    }, ms);
    return () => window.clearTimeout(id);
  }, [autoCompleteMs]);

  const name = data.firstName?.trim() ?? "";
  return (
    <div
      className={cn(
        "submit-celebration-root relative w-full overflow-hidden rounded-3xl border border-[#166B93]/10 bg-gradient-to-b from-white via-white to-[#166B93]/[0.06] px-5 py-10 shadow-[0_24px_80px_-24px_rgba(22,107,147,0.35)] sm:px-10 sm:py-14",
        className,
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={i18n._(msg`Intake submitted successfully`)}
    >
      <div
        className="submit-celebration-blob pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-[#CEF12F]/25 blur-3xl"
        aria-hidden
      />
      <div
        className="submit-celebration-blob-delay pointer-events-none absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-[#166B93]/20 blur-3xl"
        aria-hidden
      />
      <div
        className="submit-celebration-blob pointer-events-none absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FF9142]/15 blur-2xl"
        aria-hidden
      />

      <div className="relative mx-auto flex max-w-lg flex-col items-center text-center">
        <img
          src="/jemma-services-logo-full.svg"
          alt="Jema Services"
          className="mb-5 h-14 w-auto object-contain opacity-90 sm:h-16"
        />

        <div className="relative mb-4 h-16 w-full">
          {successAnimation ? (
            <Lottie
              animationData={successAnimation}
              loop={false}
              autoplay
              className="pointer-events-none absolute left-1/2 top-1/2 h-52 w-52 -translate-x-1/2 -translate-y-1/2 sm:h-64 sm:w-64"
              aria-hidden
            />
          ) : (
            <div className="pointer-events-none absolute left-1/2 top-1/2 flex h-[4.25rem] w-[4.25rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_12px_40px_-8px_rgba(22,107,147,0.45)] sm:h-[4.75rem] sm:w-[4.75rem]">
              <Check
                className="submit-celebration-check h-10 w-10 text-[#166B93] sm:h-11 sm:w-11"
                strokeWidth={2.5}
                aria-hidden
              />
            </div>
          )}
        </div>

        <h1 className="submit-celebration-fade-up font-heading text-[clamp(1.5rem,5vw,2.25rem)] font-bold tracking-tight text-gray-900">
          {name ? (
            <Trans>
              Thank you, <span className="text-[#166B93]">{name}</span>!
            </Trans>
          ) : (
            <Trans>Thank you!</Trans>
          )}
        </h1>

        <p className="submit-celebration-fade-up-delay mt-4 text-base leading-relaxed text-gray-600 sm:text-lg">
          {descriptionContent ?? (
            <Trans>
              Thank you for filling out this information. Please go to your email
              and click the link to verify this form so doctors can quote you.
            </Trans>
          )}
        </p>
        {showInboxButton ? (
          <a
            href="https://mail.google.com/mail/u/0/#inbox"
            target="_blank"
            rel="noopener noreferrer"
            className="submit-celebration-fade-up-delay mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#166B93] px-5 text-sm font-semibold text-white shadow-[0_10px_24px_-12px_rgba(22,107,147,0.65)] transition-all duration-200 ease-out hover:bg-[#145d82] hover:shadow-[0_14px_28px_-12px_rgba(22,107,147,0.72)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#166B93] sm:h-12 sm:px-6 sm:text-base"
          >
            <Trans>Go to inbox</Trans>
            <ArrowRight className="h-4 w-4" aria-hidden />
          </a>
        ) : null}

        {footerActions ? (
          <div className="submit-celebration-fade-up-delay mt-5">{footerActions}</div>
        ) : null}

        {showVerifyHint ? (
          <p className="submit-celebration-fade-up-delay mt-8 text-xs font-medium uppercase tracking-[0.2em] text-[#166B93]/70">
            <Trans>verify your form</Trans>
          </p>
        ) : null}
      </div>
    </div>
  );
});
